import { useMemo } from 'react';
import type { DraftPick } from '@ff/core';
import { useMe } from '../lib/me.tsx';
import { shortName } from '../lib/format.ts';
import { Avatar } from './Avatar.tsx';
import { ManagerLink } from './ManagerLink.tsx';

/**
 * The draft, kept for the record.
 *
 * Round one gets its own block because it's the one people argue about all
 * season — it's the round the Clown comes from, and the round everybody
 * remembers. The rest is a reference: sixteen rounds nobody reads end to end,
 * but which settles "who took him and when" in one look.
 */
export function DraftBoard({
  picks,
  nameOf,
}: {
  picks: DraftPick[];
  nameOf: (id: string) => string;
}) {
  const me = useMe();
  const rounds = useMemo(() => {
    const byRound = new Map<number, DraftPick[]>();
    for (const p of [...picks].sort((a, b) => a.overall - b.overall)) {
      const list = byRound.get(p.round) ?? [];
      list.push(p);
      byRound.set(p.round, list);
    }
    return [...byRound.entries()].sort((a, b) => a[0] - b[0]);
  }, [picks]);

  // Whose picks to pick out of a wall of 192 names. Above the early return:
  // hooks can't run conditionally.
  const myOwnerId = useMemo(() => {
    if (!me?.name) return null;
    const mine = picks.find((p) => nameOf(p.ownerId).toLowerCase() === me.name.toLowerCase());
    return mine?.ownerId ?? null;
  }, [me, picks, nameOf]);

  if (rounds.length === 0) return null;
  const [first, ...rest] = rounds;
  const firstRoundPicks = first ? first[1] : [];

  return (
    <section className="space-y-6">
      <div>
        <p className="eyebrow">Round 1</p>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {firstRoundPicks.map((p) => (
            <div
              key={p.overall}
              className={`card flex items-center gap-3 p-3 ${
                p.ownerId === myOwnerId ? 'ring-1 ring-brass/40' : ''
              }`}
            >
              <span className="w-6 shrink-0 font-mono text-sm text-chalk-dim tabular">
                {p.pickInRound}
              </span>
              <Avatar name={nameOf(p.ownerId)} size={28} />
              <div className="min-w-0 flex-1">
                <div className="truncate font-display text-sm font-bold">{p.playerName}</div>
                <div className="truncate font-mono text-[0.65rem] uppercase tracking-wider text-chalk-dim">
                  {p.position} ·{' '}
                  <ManagerLink ownerId={p.ownerId}>{nameOf(p.ownerId)}</ManagerLink>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {rest.map(([round, roundPicks]) => (
          <div key={round} className="card p-4">
            <p className="eyebrow mb-2">Round {round}</p>
            <div className="space-y-0.5">
              {roundPicks.map((p) => (
                <div
                  key={p.overall}
                  className={`flex items-baseline gap-2 font-mono text-xs ${
                    p.ownerId === myOwnerId ? 'text-brass' : ''
                  }`}
                >
                  <span className="w-5 shrink-0 text-right text-[0.65rem] text-chalk-dim tabular">
                    {p.pickInRound}
                  </span>
                  <span
                    className={`min-w-0 flex-1 truncate font-body ${
                      p.ownerId === myOwnerId ? 'text-brass' : 'text-chalk'
                    }`}
                  >
                    {p.playerName}
                    <span className="ml-1.5 text-[0.6rem] uppercase text-chalk-dim">
                      {p.position}
                    </span>
                  </span>
                  {/* "Morgan E." rather than a truncated "Morgan Elli…":
                      fifteen rounds of names need to be scannable, and an
                      ellipsis in every row is not. */}
                  <span className="w-20 shrink-0 truncate text-right text-[0.65rem] text-chalk-dim">
                    <ManagerLink ownerId={p.ownerId}>{shortName(nameOf(p.ownerId))}</ManagerLink>
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
